// src/pages/PermissionsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import AdminPageHeader from "../components/AdminPageHeader";
import useUserRole, { isSuperRole } from "../hooks/useUserRole";

const ROLE_ORDER = [
  "Super User",
  "Admin",
  "Supervisor",
  "Field User",
];

const PERMISSION_ORDER = [
  "ApplicationsRead",
  "ApplicationsEdit",
  "EmployeesRead",
  "EmployeesEdit",
  "DispatchConsole",
  "StatusBoard",
  "DailyOps",
  "FullScheduleRead",
  "FullScheduleEdit",
  "OwnSchedule",
  "EditPunches",
  "TimeCardsRead",
  "TimeCardsEdit",
  "ScheduleControl",
  "PayRates",
  "TournamentDates",
  "PermissionsEdit",
];

function sortByPreferredOrder(items, preferredOrder) {
  return [...items].sort((a, b) => {
    const ai = preferredOrder.indexOf(a);
    const bi = preferredOrder.indexOf(b);

    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;

    return ai - bi;
  });
}

export default function PermissionsPage() {
  const { role, loading: roleLoading } = useUserRole();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [roles, setRoles] = useState([]);
  const [roleDocs, setRoleDocs] = useState({});

  const rolesRef = collection(db, "role_permissions");

  useEffect(() => {
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadPermissions() {
    setLoading(true);
    setError("");

    try {
      const snap = await getDocs(rolesRef);
      const docsMap = {};
      const roleNames = [];

      snap.forEach((d) => {
        docsMap[d.id] = d.data() || {};
        roleNames.push(d.id);
      });

      setRoles(sortByPreferredOrder(roleNames, ROLE_ORDER));
      setRoleDocs(docsMap);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Failed to load permissions.");
    } finally {
      setLoading(false);
    }
  }

  const permissionKeys = useMemo(() => {
    const keys = new Set();

    Object.values(roleDocs).forEach((docData) => {
      Object.keys(docData || {}).forEach((key) => keys.add(key));
    });

    return sortByPreferredOrder(Array.from(keys), PERMISSION_ORDER);
  }, [roleDocs]);

  async function handleToggle(roleName, key, value) {
    try {
      setSaving(true);
      setError("");

      const ref = doc(db, "role_permissions", roleName);
      await updateDoc(ref, { [key]: value });

      setRoleDocs((prev) => ({
        ...prev,
        [roleName]: {
          ...(prev[roleName] || {}),
          [key]: value,
        },
      }));
    } catch (e) {
      console.error(e);
      setError(e?.message || "Failed to update permission.");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddRole() {
    const name = (window.prompt("New role name:") || "").trim();
    if (!name) return;

    if (roles.includes(name)) {
      window.alert("That role already exists.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      await setDoc(doc(db, "role_permissions", name), {});

      setRoles((prev) => sortByPreferredOrder([...prev, name], ROLE_ORDER));
      setRoleDocs((prev) => ({
        ...prev,
        [name]: {},
      }));
    } catch (e) {
      console.error(e);
      setError(e?.message || "Failed to add role.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteRole(roleName) {
    if (!window.confirm(`Delete role "${roleName}"?`)) return;

    try {
      setSaving(true);
      setError("");

      await deleteDoc(doc(db, "role_permissions", roleName));

      setRoles((prev) => prev.filter((r) => r !== roleName));
      setRoleDocs((prev) => {
        const next = { ...prev };
        delete next[roleName];
        return next;
      });
    } catch (e) {
      console.error(e);
      setError(e?.message || "Failed to delete role.");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddPermission() {
    const key = (window.prompt("New permission key:") || "").trim();
    if (!key) return;

    if (permissionKeys.includes(key)) {
      window.alert("That permission already exists.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      await Promise.all(
        roles.map((roleName) =>
          setDoc(
            doc(db, "role_permissions", roleName),
            { [key]: false },
            { merge: true }
          )
        )
      );

      setRoleDocs((prev) => {
        const next = { ...prev };
        roles.forEach((roleName) => {
          next[roleName] = {
            ...(next[roleName] || {}),
            [key]: false,
          };
        });
        return next;
      });
    } catch (e) {
      console.error(e);
      setError(e?.message || "Failed to add permission.");
    } finally {
      setSaving(false);
    }
  }

  if (roleLoading || loading) {
    return <div style={{ padding: 24 }}>Loading permissions…</div>;
  }

  if (!role || (!isSuperRole(role) && role !== "Admin")) {
    return (
      <div style={{ padding: 24 }}>
        <AdminPageHeader title="Permissions" level="h2" />
        <p>You do not have access to this page.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <AdminPageHeader title="Permissions" level="h2" />

      <p>Manage role-based access for the portal.</p>

      {error ? (
        <div style={{ color: "crimson", marginBottom: 12 }}>Error: {error}</div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {isSuperRole(role) && (
          <>
            <button onClick={handleAddRole} disabled={saving}>
              Add Role
            </button>
            <button onClick={handleAddPermission} disabled={saving}>
              Add Permission
            </button>
          </>
        )}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            minWidth: 900,
            background: "#fff",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: 10,
                  borderBottom: "1px solid #ddd",
                  position: "sticky",
                  left: 0,
                  background: "#fff",
                  zIndex: 2,
                }}
              >
                Role
              </th>

              {permissionKeys.map((key) => (
                <th
                  key={key}
                  style={{
                    textAlign: "center",
                    padding: 10,
                    borderBottom: "1px solid #ddd",
                    whiteSpace: "nowrap",
                  }}
                >
                  {key}
                </th>
              ))}

              {isSuperRole(role) && (
                <th
                  style={{
                    textAlign: "center",
                    padding: 10,
                    borderBottom: "1px solid #ddd",
                    whiteSpace: "nowrap",
                  }}
                >
                  Delete Role
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {roles.map((roleName) => (
              <tr key={roleName}>
                <td
                  style={{
                    padding: 10,
                    borderBottom: "1px solid #eee",
                    position: "sticky",
                    left: 0,
                    background: "#fff",
                    fontWeight: 600,
                    zIndex: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {roleName}
                </td>

                {permissionKeys.map((key) => {
                  const checked = Boolean(roleDocs?.[roleName]?.[key]);

                  return (
                    <td
                      key={`${roleName}-${key}`}
                      style={{
                        textAlign: "center",
                        padding: 10,
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={saving || !isSuperRole(role)}
                        onChange={(e) =>
                          handleToggle(roleName, key, e.target.checked)
                        }
                      />
                    </td>
                  );
                })}

                {isSuperRole(role) && (
                  <td
                    style={{
                      textAlign: "center",
                      padding: 10,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <button
                      onClick={() => handleDeleteRole(roleName)}
                      disabled={saving}
                    >
                      Delete Role
                    </button>
                  </td>
                )}
              </tr>
            ))}

            {roles.length === 0 && (
              <tr>
                <td
                  colSpan={permissionKeys.length + (isSuperRole(role) ? 2 : 1)}
                  style={{ padding: 16 }}
                >
                  No roles found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}